import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "../lib/supabase";
import { useAuth, markExplicitSignOut } from "../context/AuthContext";
import { ConsentModal } from "./ConsentModal";
import { fetchConsent, postConsent, CONSENT_VERSION } from "../lib/consent";

const STATE_KEY = (uid) => `dropit_consent_state:${uid}`;

/* Mounted inside ProtectedRoute: syncs a pre-auth local consent to the account, then blocks until the current version is on record.
   Fails open when the API is unreachable (offline dispatchers keep working; re-checked on the next online boot). */
export const ConsentGate = () => {
  const { session, user } = useAuth();
  const navigate = useNavigate();
  const [needs, setNeeds] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!session?.user) return;
    const uid = session.user.id;
    let alive = true;
    (async () => {
      try {
        const pending = JSON.parse(localStorage.getItem("dropit_consent_pending") || "null");
        if (pending?.version === CONSENT_VERSION && pending.email === (session.user.email || "").toLowerCase()) {
          localStorage.removeItem("dropit_consent_pending");  // claim it first so a concurrent mount doesn't double-post
          const created = new Date(session.user.created_at || 0).getTime();
          try { await postConsent({ source: Date.now() - created < 10 * 60 * 1000 ? "signup" : "relogin", clientAt: pending.at }); }
          catch (e) { localStorage.setItem("dropit_consent_pending", JSON.stringify(pending)); throw e; }
        }
        const c = await fetchConsent();
        localStorage.setItem(STATE_KEY(uid), JSON.stringify({ needs: c.needs_consent, at: Date.now() }));
        if (alive) setNeeds(c.needs_consent);
      } catch {
        const cached = JSON.parse(localStorage.getItem(STATE_KEY(uid)) || "null");  // offline → last known state, never trap
        if (alive) setNeeds(cached?.needs === true && navigator.onLine);
      }
    })();
    return () => { alive = false; };
  }, [session?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!session?.user || !needs) return null;
  const confirm = async () => {
    setBusy(true);
    try { await postConsent({ source: "gate" }); localStorage.setItem(STATE_KEY(user.id), JSON.stringify({ needs: false, at: Date.now() })); setNeeds(false); }
    catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const signOut = async () => { markExplicitSignOut(); await supabase.auth.signOut({ scope: "local" }); navigate("/auth", { replace: true }); };
  return <ConsentModal open mode="gate" busy={busy} onConfirm={confirm} onSignOut={signOut} />;
};
